import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { clearLocalCart } from "./CartDrawer";
import {
    addTrackedOrder,
    getOrderNumber,
    getOrderStatusKey,
} from "../utils/lastOrder";
import {
    PAYMENT_CHECK_EVENT,
    clearPendingPayment,
    getPendingPayment,
    hasConfirmedPaymentAttempt,
    parseRazorpayCallbackParams,
    recoverPendingPayment,
} from "../utils/pendingPayment";

const isOrderResultPath = (pathname = "") =>
    /\/(success|order-status|orders)\/?$/.test(pathname);

const shouldShowConfirmOverlay = () => {
    const callback = parseRazorpayCallbackParams();
    const pending = getPendingPayment();

    return Boolean(
        (callback.razorpayPaymentId && callback.razorpaySignature) ||
            hasConfirmedPaymentAttempt(pending),
    );
};

const PendingPaymentRecovery = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const locationRef = useRef(location);
    const inFlightRef = useRef(false);
    const unpaidMissesRef = useRef(0);
    const [isRecovering, setIsRecovering] = useState(shouldShowConfirmOverlay);

    locationRef.current = location;

    const applyRecoveredOrder = (order, session) => {
        const razorpayOrderId = session?.razorpayOrderId;
        const outletId =
            session?.outletId ||
            session?.payload?.outlet ||
            localStorage.getItem("outletId");

        if (outletId && razorpayOrderId) {
            addTrackedOrder(outletId, razorpayOrderId, {
                orderNumber: getOrderNumber(order),
                status: getOrderStatusKey(order),
                placedAt: order?.createdAt || new Date().toISOString(),
            });
        }

        clearLocalCart();
        clearPendingPayment(razorpayOrderId);

        if (!outletId || !razorpayOrderId) return;

        const currentPath = locationRef.current?.pathname || "";
        if (isOrderResultPath(currentPath)) return;

        navigate(`/${outletId}/success`, {
            replace: true,
            state: { razorpayOrderId },
        });
    };

    const runRecovery = async ({
        showOverlay = false,
        pollAttempts,
        pollDelayMs,
        abandonIfUnpaid = false,
    } = {}) => {
        if (inFlightRef.current) return;

        const pending = getPendingPayment();
        const callback = parseRazorpayCallbackParams();
        const hasWork = Boolean(
            pending || callback.razorpayOrderId || callback.razorpayPaymentId,
        );

        if (!hasWork) {
            setIsRecovering(false);
            return;
        }

        const confirmed =
            shouldShowConfirmOverlay() || hasConfirmedPaymentAttempt(pending);
        const onResultPage = isOrderResultPath(
            locationRef.current?.pathname || "",
        );
        const shouldShowOverlay =
            showOverlay && confirmed && !onResultPage && !abandonIfUnpaid;

        inFlightRef.current = true;

        if (shouldShowOverlay) {
            setIsRecovering(true);
        } else {
            setIsRecovering(false);
        }

        try {
            const result = await recoverPendingPayment({
                pollAttempts,
                pollDelayMs,
                abandonIfUnpaid: abandonIfUnpaid && !confirmed,
            });

            if (result.status === "created") {
                unpaidMissesRef.current = 0;
                applyRecoveredOrder(result.order, result.session);
                return;
            }

            if (result.status === "abandoned" || result.status === "none") {
                unpaidMissesRef.current = 0;
                setIsRecovering(false);
                return;
            }

            if (!hasConfirmedPaymentAttempt(result.session)) {
                unpaidMissesRef.current += 1;
                if (unpaidMissesRef.current >= 2) {
                    clearPendingPayment(result.session?.razorpayOrderId);
                    unpaidMissesRef.current = 0;
                }
            }
        } catch (error) {
            console.error("Pending payment recovery:", error);
        } finally {
            inFlightRef.current = false;
            setIsRecovering(false);
        }
    };

    useEffect(() => {
        runRecovery({
            showOverlay: false,
            pollAttempts: 6,
            pollDelayMs: 1000,
        });

        const onReturnToApp = () => {
            if (
                typeof document !== "undefined" &&
                document.visibilityState &&
                document.visibilityState !== "visible"
            ) {
                return;
            }

            const confirmed = shouldShowConfirmOverlay();

            runRecovery({
                showOverlay: confirmed,
                pollAttempts: confirmed ? 12 : 4,
                pollDelayMs: confirmed ? 1500 : 800,
            });
        };

        const onPaymentCheck = (event) => {
            const cancelled = Boolean(event?.detail?.cancelled);
            const confirmed = shouldShowConfirmOverlay();

            runRecovery({
                showOverlay: !cancelled && confirmed,
                abandonIfUnpaid: cancelled && !confirmed,
                pollAttempts: cancelled ? 2 : confirmed ? 12 : 4,
                pollDelayMs: cancelled ? 600 : 1500,
            });
        };

        document.addEventListener("visibilitychange", onReturnToApp);
        window.addEventListener("pageshow", onReturnToApp);
        window.addEventListener("online", onReturnToApp);
        window.addEventListener(PAYMENT_CHECK_EVENT, onPaymentCheck);

        return () => {
            document.removeEventListener("visibilitychange", onReturnToApp);
            window.removeEventListener("pageshow", onReturnToApp);
            window.removeEventListener("online", onReturnToApp);
            window.removeEventListener(PAYMENT_CHECK_EVENT, onPaymentCheck);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!isRecovering) return null;

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                zIndex: 3000,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                backgroundColor: "rgba(12, 42, 58, 0.72)",
                backdropFilter: "blur(4px)",
            }}
        >
            <CircularProgress sx={{ color: "#FFFFFF" }} />
            <Typography
                sx={{
                    color: "#FFFFFF",
                    fontSize: "16px",
                    fontWeight: 700,
                    textAlign: "center",
                    px: 3,
                }}
            >
                Confirming your payment...
            </Typography>
            <Typography
                sx={{
                    color: "rgba(255,255,255,0.82)",
                    fontSize: "13px",
                    fontWeight: 500,
                    textAlign: "center",
                    px: 4,
                }}
            >
                Please wait while we place your order.
            </Typography>
        </Box>
    );
};

export default PendingPaymentRecovery;
